import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { UiEmptyStateComponent, UIIconComponent, UiLoadingComponent } from '@design-system';
import { IconName } from '@icon/assets';
import { SymbolMessage } from '../../../../models/symbol-message.model';
import { MessagesService } from '../../../../services/messages.service';
import { SymbolMessageCardComponent } from './symbol-message-card/symbol-message-card.component';

/** Messages sharing one jalali day, under a single sticky day header. */
export interface SymbolMessageDayGroup {
	/**
	 * Identifies the run for `@for`'s `track`. The date alone will not do: a feed that
	 * returns a day it has already been past opens a second group with the same date, and
	 * two groups tracked by the same key make Angular reconcile the wrong one. The first
	 * message's id is unique across the feed, so it is unique across the runs too.
	 */
	key: string;
	date: string;
	messages: SymbolMessage[];
}

/**
 * Groups consecutive messages by day, keeping the order the feed returned them in
 * (newest first) rather than sorting — the day header follows the list, so re-ordering
 * here would silently disagree with the server's own ordering.
 */
export function groupMessagesByDay(messages: readonly SymbolMessage[]): SymbolMessageDayGroup[] {
	const groups: SymbolMessageDayGroup[] = [];

	for (const message of messages) {
		const current = groups.at(-1);
		if (current?.date === message.date) {
			current.messages.push(message);
		} else {
			groups.push({ key: `${message.date}#${message.id}`, date: message.date, messages: [message] });
		}
	}

	return groups;
}

@Component({
	selector: 'app-symbol-messages',
	imports: [SymbolMessageCardComponent, UiEmptyStateComponent, UiLoadingComponent, UIIconComponent],
	templateUrl: './symbol-messages.component.html',
	host: {
		class: 'flex min-h-0 w-full flex-1 flex-col'
	}
})
export class SymbolMessagesComponent {
	private readonly messagesService = inject(MessagesService);

	readonly symbolIsin = input.required<string>();

	protected readonly IconName = IconName;

	private readonly messagesResource = rxResource({
		params: () => this.symbolIsin(),
		stream: ({ params: symbolIsin }) => this.messagesService.getSymbolMessages(symbolIsin)
	});

	readonly loading = this.messagesResource.isLoading;
	readonly hasError = computed(() => !!this.messagesResource.error());

	readonly dayGroups = computed(() => groupMessagesByDay(this.messagesResource.value() ?? []));

	readonly isEmpty = computed(() => !this.loading() && !this.hasError() && this.dayGroups().length === 0);

	onRetry(): void {
		this.messagesResource.reload();
	}
}
